import { useState, useEffect, useCallback } from "react";

/**
 * Hook that calculates optimal font scale for kiosk display
 * based on viewport size and content amount.
 *
 * Goal: Fit all content on screen without scrolling, scaling text
 * larger when there's less content and smaller when there's more.
 */
export function useKioskFontScale(
  entryCount: number,
  absentTeacherCount: number = 0,
) {
  const [scale, setScale] = useState(1);

  const calculateScale = useCallback(() => {
    // Get viewport dimensions
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    // Base row height estimate (in pixels) at scale 1.0
    const baseRowHeight = 28;

    // Available height for the table (accounting for padding and header)
    const availableHeight = vh - 100; // Subtract header/padding

    // Calculate how many rows can fit at scale 1.0
    const maxRowsAtScale1 = Math.floor(availableHeight / baseRowHeight);

    // Account for multi-column layout (table renders max 3 columns)
    const columns = vw > 1400 ? 3 : vw > 900 ? 2 : 1;
    const effectiveCapacity = maxRowsAtScale1 * columns;

    // Calculate content density ratio
    // If entries fit easily, scale up. If too many, scale down.
    const totalContent = entryCount + Math.ceil(absentTeacherCount / 4); // Rough estimate

    if (totalContent === 0) {
      // No content, use generous scale
      setScale(1.8);
      return;
    }

    // Calculate the ratio of capacity to content
    const capacityRatio = effectiveCapacity / totalContent;

    // Convert ratio to scale factor - use capacity ratio more directly
    // Scale linearly with available space, clamped to reasonable bounds
    // capacityRatio of 1.0 = just fits at scale 1.0
    // capacityRatio of 2.0 = double the space, can scale up

    // Direct scaling: scale = sqrt(capacityRatio) for balanced growth
    let newScale = Math.sqrt(capacityRatio) * 0.95;

    // Clamp to reasonable bounds
    newScale = Math.max(0.7, Math.min(1.8, newScale));

    setScale(newScale);
  }, [entryCount, absentTeacherCount]);

  // Recalculate on mount and when content changes
  useEffect(() => {
    calculateScale();
  }, [calculateScale]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      calculateScale();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateScale]);

  return scale;
}
